import React from 'react';
import { BarChart3, BookOpen, Users, Eye, TrendingUp, CheckCircle, Clock } from 'lucide-react';
import { Button } from '../ui/button';

/**
 * ReadingAnalyticsPanel — admin Reading Analytics summary + per-book
 * detail table. Pure presentation: parent owns the analytics payload,
 * the auto-refresh timestamp, and the "open detail modal" handler.
 *
 * Extracted from AdminSettings.jsx (2026-05-30) to bring that page
 * below the 750-line health threshold.
 *
 * Props:
 *   analytics             — { summary, analytics: [] } | null
 *   loading               — bool, shows skeleton
 *   analyticsLastFetched  — number (Date.now()) of last successful fetch
 *   analyticsTick         — incremented every 1s so "Refreshed Xs ago" repaints
 *   onRefresh             — () => void, forces a fetch
 *   onOpenBookDetail      — (book_id) => void, opens BookAnalyticsDetailModal
 */
const ReadingAnalyticsPanel = ({
  analytics,
  loading,
  analyticsLastFetched,
  analyticsTick,
  onRefresh,
  onOpenBookDetail,
}) => {
  return (
    <>
      {/* Analytics Summary Section */}
      <div className="bg-white/5 rounded-xl border border-white/10 p-6" data-testid="analytics-summary-section">
        <div className="flex items-center mb-6">
          <BarChart3 className="h-6 w-6 text-blue-500 mr-2" />
          <h2 className="text-xl font-bold text-white">Reading Analytics</h2>
        </div>

        {loading ? (
          <div className="text-center py-8 text-gray-400">Loading analytics...</div>
        ) : analytics ? (
          <>
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-white/5 rounded-lg p-4 text-center">
                <BookOpen className="h-6 w-6 text-purple-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-white">{analytics.summary.total_books}</p>
                <p className="text-xs text-gray-400">Total Books</p>
              </div>
              <div className="bg-white/5 rounded-lg p-4 text-center">
                <Users className="h-6 w-6 text-blue-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-white">{analytics.summary.total_reading_sessions}</p>
                <p className="text-xs text-gray-400">Reading Sessions</p>
              </div>
              <div className="bg-white/5 rounded-lg p-4 text-center">
                <CheckCircle className="h-6 w-6 text-green-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-white">{analytics.summary.total_completions}</p>
                <p className="text-xs text-gray-400">Completions</p>
              </div>
            </div>

            {analytics.analytics.length === 0 && (
              <div className="text-center py-4 text-gray-500">
                No reading data yet. Analytics will appear once users start reading books.
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-8 text-gray-500">Failed to load analytics</div>
        )}
      </div>

      {/* Detailed Analytics Table */}
      {analytics && analytics.analytics.length > 0 && (
        <div className="mt-8 bg-white/5 rounded-xl border border-white/10 overflow-hidden" data-testid="analytics-table">
          <div className="p-6 border-b border-white/10 flex items-center justify-between gap-4 flex-wrap">
            <h2 className="text-xl font-bold text-white flex items-center">
              <TrendingUp className="h-6 w-6 text-green-500 mr-2" />
              Per-Book Analytics
            </h2>
            <div className="flex items-center gap-2 text-xs" data-testid="analytics-live-indicator">
              <span className="relative flex h-2 w-2" aria-hidden="true">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
              </span>
              <span className="text-emerald-400 font-medium uppercase tracking-wider">Live</span>
              <span className="text-gray-500" data-testid="analytics-last-fetched">
                · {(() => {
                  // Reading `analyticsTick` here keeps the "Refreshed Xs ago"
                  // label re-rendering every second without an internal timer.
                  const _ = analyticsTick; // eslint-disable-line no-unused-vars
                  if (!analyticsLastFetched) return 'updating…';
                  const sec = Math.max(0, Math.floor((Date.now() - analyticsLastFetched) / 1000));
                  if (sec < 5) return 'just now';
                  if (sec < 60) return `Refreshed ${sec}s ago`;
                  const mins = Math.floor(sec / 60);
                  return `Refreshed ${mins}m ago`;
                })()}
              </span>
              <button
                type="button"
                onClick={onRefresh}
                className="ml-1 px-2 py-0.5 rounded-md text-gray-300 hover:text-white hover:bg-white/10 transition-colors"
                title="Refresh now"
                data-testid="analytics-refresh-now"
              >
                ↻
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-white/5">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Book</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-300">Total Readers</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-300">
                    <span className="flex items-center justify-center">
                      <Clock className="h-4 w-4 mr-1 text-yellow-400" />
                      Started
                    </span>
                  </th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-300">
                    <span className="flex items-center justify-center">
                      <Eye className="h-4 w-4 mr-1 text-blue-400" />
                      Partial
                    </span>
                  </th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-300">
                    <span className="flex items-center justify-center">
                      <CheckCircle className="h-4 w-4 mr-1 text-green-400" />
                      Completed
                    </span>
                  </th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-300">Avg Progress</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-gray-300">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {analytics.analytics.map((item) => (
                  <tr key={item.book_id} className="hover:bg-white/5 transition-colors">
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-white font-medium">{item.title}</p>
                        <p className="text-gray-400 text-sm">{item.author}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="text-white font-semibold">{item.total_readers}</span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="px-2 py-1 bg-yellow-500/20 text-yellow-300 rounded text-sm">
                        {item.started}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="px-2 py-1 bg-blue-500/20 text-blue-300 rounded text-sm">
                        {item.partial_reads}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="px-2 py-1 bg-green-500/20 text-green-300 rounded text-sm">
                        {item.completed_reads}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex items-center justify-center">
                        <div className="w-16 h-2 bg-white/10 rounded-full overflow-hidden mr-2">
                          <div
                            className="h-full bg-gradient-to-r from-blue-500 to-green-500 rounded-full"
                            style={{ width: `${item.average_progress}%` }}
                          />
                        </div>
                        <span className="text-gray-400 text-sm">{item.average_progress}%</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onOpenBookDetail(item.book_id)}
                        className="text-gray-400 hover:text-white hover:bg-white/10"
                        data-testid={`view-details-${item.book_id}`}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        Details
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
};

export default ReadingAnalyticsPanel;
